<template>
  <div class="catalog-price-form">
    <div class="editor-grid">
      <label>
        Price logic
        <select
          class="regular bordered"
          :value="value.priceLogic"
          :disabled="readonly"
          @change="update('priceLogic', $event.target.value)"
        >
          <option value="fixed">Fixed</option>
          <option value="calculated">Calculated</option>
        </select>
      </label>
      <label>
        Price group
        <select
          class="regular bordered"
          :value="value.priceGroup"
          :disabled="readonly"
          @change="update('priceGroup', $event.target.value)"
        >
          <option value="">—</option>
          <option v-for="g in priceGroups" :key="g.id" :value="g.name">
            {{ g.name }}
          </option>
        </select>
      </label>
      <label>
        Cost
        <input
          class="regular bordered"
          type="number"
          step="0.01"
          :value="value.cost"
          :disabled="readonly"
          @input="update('cost', toNumber($event.target.value))"
        />
      </label>
      <label v-if="isLp45">
        Dealer profit
        <input
          class="regular bordered"
          type="number"
          step="0.01"
          :value="dealerProfit"
          disabled
          title="Global setting (lp45_dealer_profit), edit it from Catalog Pricing"
        />
      </label>
      <label>
        List price
        {{
          isLp45
            ? "(calculated)"
            : masterListPriceEditable
            ? "(new product, not in part_info_local yet)"
            : "(part_info_local)"
        }}
        <input
          class="regular bordered"
          type="number"
          step="0.01"
          :value="effectiveListPrice"
          :disabled="isLp45 || !masterListPriceEditable"
          @input="
            $emit('update:masterListPrice', toNumber($event.target.value))
          "
        />
      </label>
    </div>

    <p v-if="value.priceLogic === 'calculated'" class="hint">
      Online / Repair shop / Wholesale are computed as
      <b>list_price × (1 − price_group discount)</b> (floored at min price).
      <template v-if="isLp45">
        For LP45, list_price = cost × dealer_profit × 2 (dealer_profit is a
        single global setting, not per-product).
      </template>
      <template v-else-if="masterListPriceEditable">
        For other price groups, enter list_price above (the product doesn't
        exist in part_info_local yet, so it will be saved when the product is
        created).
      </template>
      <template v-else>
        For other price groups, list_price comes from part_info_local.list_price
        (populated by Infoauto).
      </template>
      precio4-6 stay fixed.
    </p>

    <table class="tiers">
      <thead>
        <tr>
          <th>Tier</th>
          <th>Price</th>
          <th>Min price (floor)</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="tier in tiers" :key="tier.key">
          <td>{{ tier.label }}</td>
          <td>
            <input
              class="regular bordered"
              type="number"
              step="0.01"
              :value="value[tier.key]"
              :disabled="
                readonly ||
                  (tier.calculated && value.priceLogic === 'calculated')
              "
              @input="update(tier.key, toNumber($event.target.value))"
            />
          </td>
          <td>
            <input
              class="regular bordered"
              type="number"
              step="0.01"
              :value="value[tier.minKey]"
              :disabled="readonly"
              @input="update(tier.minKey, toNumber($event.target.value))"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  LP45_GROUP_NAME,
  DEFAULT_DEALER_PROFIT,
  calculateLp45ListPrice,
  calculateTiersFromListPrice
} from "@/utils/priceGroupCalculator.js";

export const CATALOG_TIERS = [
  {
    key: "online",
    minKey: "onlineMinPrice",
    label: "Online",
    calculated: true
  },
  {
    key: "repairShop",
    minKey: "repairShopMinPrice",
    label: "Repair shop",
    calculated: true
  },
  {
    key: "wholesale",
    minKey: "wholesaleMinPrice",
    label: "Wholesale",
    calculated: true
  },
  {
    key: "precio4",
    minKey: "precio4MinPrice",
    label: "Precio 4",
    calculated: false
  },
  {
    key: "precio5",
    minKey: "precio5MinPrice",
    label: "Precio 5",
    calculated: false
  },
  {
    key: "precio6",
    minKey: "precio6MinPrice",
    label: "Precio 6",
    calculated: false
  }
];

export function emptyCatalogDraft() {
  return {
    priceLogic: "fixed",
    priceGroup: "",
    cost: 0,
    online: 0,
    repairShop: 0,
    wholesale: 0,
    precio4: 0,
    precio5: 0,
    precio6: 0,
    onlineMinPrice: 0,
    repairShopMinPrice: 0,
    wholesaleMinPrice: 0,
    precio4MinPrice: 0,
    precio5MinPrice: 0,
    precio6MinPrice: 0
  };
}

export default {
  name: "CatalogPriceForm",
  props: {
    value: {
      type: Object,
      required: true
    },
    priceGroups: {
      type: Array,
      default: () => []
    },
    // list_price maestro de part_info_local (poblado por Infoauto/pvp).
    // Se usa como base de descuento para cualquier price_group que no sea LP45.
    masterListPrice: {
      type: Number,
      default: 0
    },
    // true en el flujo de creación de producto: part_info_local todavía no
    // existe, así que list_price se ingresa acá y el padre lo manda al crear
    // el producto (ver NewProductModal.vue).
    masterListPriceEditable: {
      type: Boolean,
      default: false
    },
    // Setting global (settings.lp45_dealer_profit), no editable por producto.
    // Solo aplica cuando priceGroup === 'LP45'.
    dealerProfit: {
      type: Number,
      default: DEFAULT_DEALER_PROFIT
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tiers: CATALOG_TIERS
    };
  },
  computed: {
    isLp45() {
      return this.value.priceGroup === LP45_GROUP_NAME;
    },
    effectiveListPrice() {
      return this.isLp45
        ? calculateLp45ListPrice(this.value.cost, this.dealerProfit)
        : this.masterListPrice;
    }
  },
  watch: {
    // Mantiene los tiers calculados en sincronía con list_price efectivo /
    // price_group / min prices mientras priceLogic = 'calculated'.
    // El backend vuelve a materializar todo al guardar; esto es solo preview.
    "value.priceLogic": "recalculate",
    "value.priceGroup": "recalculate",
    "value.cost": "recalculate",
    "value.onlineMinPrice": "recalculate",
    "value.repairShopMinPrice": "recalculate",
    "value.wholesaleMinPrice": "recalculate",
    masterListPrice: "recalculate",
    dealerProfit: "recalculate"
  },
  methods: {
    update(key, val) {
      this.$emit("input", { ...this.value, [key]: val });
    },
    toNumber(v) {
      return v === "" ? 0 : Number(v);
    },
    recalculate() {
      if (this.value.priceLogic !== "calculated") return;

      const group = this.priceGroups.find(
        g => g.name === this.value.priceGroup
      );
      const computedTiers = calculateTiersFromListPrice(
        this.effectiveListPrice,
        group,
        {
          online: this.value.onlineMinPrice,
          repairShop: this.value.repairShopMinPrice,
          wholesale: this.value.wholesaleMinPrice
        }
      );

      this.$emit("input", {
        ...this.value,
        online: computedTiers.online,
        repairShop: computedTiers.repairShop,
        wholesale: computedTiers.wholesale
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.catalog-price-form {
  .editor-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 10px;
    label {
      display: flex;
      flex-direction: column;
      font-size: 0.85rem;
      gap: 4px;
    }
  }
  .hint {
    font-size: 0.8rem;
    color: #555;
    margin-bottom: 8px;
  }
  .tiers {
    width: 100%;
    border-collapse: collapse;
    th,
    td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      text-align: left;
    }
    input {
      width: 120px;
    }
    input:disabled {
      background: #eee;
    }
  }
}
</style>
